/**
 * bubu-tanda.js — única fuente de verdad de la fecha de salida del lote.
 *
 * Lee la configuración de <script data-bubu-tanda-config> (ajustes del tema:
 * día de salida, día de corte, plazo, festivos) y repinta cualquier
 * [data-tanda="fecha|fechaCorta|plazo|plazoCorto|chip|saleEsc|saleEscCorta|etiqueta"]
 * que haya en la página: barra superior, franja de la escalera, bloque de la ficha,
 * «Monta tu caja» y carrito dicen siempre lo mismo el mismo día.
 *
 * API: window.BubuTanda.calcula() → { salida: Date, etiqueta: 'Lun 14', iso, estado }
 *      window.BubuTanda.repinta(raiz?)
 *      document.addEventListener('bubu:tanda', e => e.detail)
 */
const DIA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DIA_MS = 86400000;

function leeConfig() {
  const el = document.querySelector('[data-bubu-tanda-config]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (_) {
    return null;
  }
}

function festivosSet(txt) {
  const s = new Set();
  String(txt || '')
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .forEach((x) => {
      // Acepta 2026-12-25 o 25/12/2026
      const m = x.match(/^(\d{4})-(\d{2})-(\d{2})$/) || x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) return;
      const iso = m[1].length === 4 ? `${m[1]}-${m[2]}-${m[3]}` : `${m[3]}-${m[2]}-${m[1]}`;
      s.add(iso);
    });
  return s;
}

function isoLocal(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function calcula(cfg, ahora) {
  const hoy = ahora || new Date();
  const g = hoy.getDay();
  const salidaW = Number(cfg.diaSalida ?? 1);
  const corteW = Number(cfg.diaCorte ?? 0);
  let add = (salidaW - g + 7) % 7;
  if (add === 0) add = 7;
  let salida = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + add);
  const fest = festivosSet(cfg.festivos);
  let guard = 0;
  while (fest.has(isoLocal(salida)) && guard < 14) {
    salida = new Date(salida.getTime() + DIA_MS);
    guard++;
  }
  const hastaCorte = (corteW - g + 7) % 7;
  const estado = hastaCorte === 0 ? 'corte' : hastaCorte <= 2 ? 'cerca' : 'normal';
  const etiqueta = `${DIA[salida.getDay()]} ${salida.getDate()}`;
  return { salida, etiqueta, iso: isoLocal(salida), estado, add, hastaCorte };
}

function cuentaAtras(cfg, ahora) {
  const d = ahora || new Date();
  const corteW = Number(cfg.diaCorte ?? 0);
  const fin = new Date(d);
  fin.setDate(d.getDate() + ((corteW - d.getDay() + 7) % 7));
  fin.setHours(23, 59, 59, 0);
  let ms = fin - d;
  if (ms < 0) ms += 7 * DIA_MS;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h} h ${m} m`;
}

function textos(cfg, t, ahoraDia) {
  const manana = t.estado === 'corte' && t.add === 1;
  const rep = (txt, def) => String(txt || def).replace('[fecha]', t.etiqueta);
  // Viernes y sábado no dicen lo mismo. Se mira el DÍA REAL, no el estado: con
  // estadoTanda forzado a «cerca» un miércoles sale el texto del viernes, que es el
  // caso por defecto, pero el sábado de verdad tiene que decir «Queda 1 día».
  const esSabado = (ahoraDia || new Date()).getDay() === 6;
  const chip = manana
    ? String(cfg.chipQuedan || 'Quedan [tiempo]').replace('[tiempo]', cuentaAtras(cfg))
    : t.estado === 'cerca'
      ? esSabado
        ? cfg.chipSab || 'Queda 1 día'
        : cfg.chipVie || 'Quedan 2 días'
      : cfg.chipHoy || 'Pídelo hoy';
  return {
    // Frase de salida. Lo único que cambia entre anchos es «próximo» / «próx.».
    fecha: manana ? cfg.textoManana : rep(cfg.texto, 'Siguiente lote sale el próximo [fecha]'),
    fechaCorta: manana ? cfg.textoManana : rep(cfg.textoCorto, 'Siguiente lote sale el próx. [fecha]'),
    // Condensada: franja de la escalera, «Monta tu caja» y carrito.
    saleEsc: manana ? cfg.textoEscManana : rep(cfg.textoEsc, 'Siguiente lote sale: [fecha]'),
    saleEscCorta: manana ? cfg.textoEscMananaCorto : rep(cfg.textoEscCorto, 'Sale el [fecha]'),
    plazo: cfg.plazo || 'Llega en 24–48 h',
    plazoCorto: cfg.plazoCorto || 'Llega 24–48 h',
    chip,
    etiqueta: t.etiqueta,
  };
}

const BubuTanda = {
  cfg: null,
  calcula() {
    if (!this.cfg) this.cfg = leeConfig() || {};
    return calcula(this.cfg);
  },
  textos() {
    if (!this.cfg) this.cfg = leeConfig() || {};
    return textos(this.cfg, this.calcula());
  },
  repinta(raiz) {
    const tx = this.textos();
    const t = this.calcula();
    (raiz || document).querySelectorAll('[data-tanda]').forEach((el) => {
      const k = el.getAttribute('data-tanda');
      if (tx[k] != null && el.textContent !== tx[k]) el.textContent = tx[k];
    });
    // El hueco del relevo tiene ancho fijo, dimensionado al más largo de los dos
    // mensajes de ESE día: así la línea no se mueve ni un píxel al cambiar.
    // SOLO el día de corte necesita el hueco ancho, porque la cuenta atrás es larga
    // («Quedan 31 h 12 m»). Viernes y sábado, con «Quedan 2 días» y «Queda 1 día», el
    // más largo vuelve a ser «Llega en 24–48 h» y comparten hueco con lunes-jueves.
    const largo = t.estado === 'corte';
    (raiz || document).querySelectorAll('[data-bubu-relevo]').forEach((el) => {
      el.classList.toggle('is-largo', largo);
    });
    document.dispatchEvent(new CustomEvent('bubu:tanda', { detail: { ...this.calcula(), textos: tx } }));
  },
};

window.BubuTanda = BubuTanda;

function arranca() {
  BubuTanda.cfg = leeConfig() || {};
  BubuTanda.repinta();
  // El día de corte la cuenta atrás avanza cada 30 s.
  setInterval(() => {
    if (BubuTanda.calcula().estado === 'corte') BubuTanda.repinta();
  }, 30000);
  // Cuando el Section Rendering API repinta el carrito, se vuelve a aplicar.
  document.addEventListener('bubu:seccion-repintada', (e) => BubuTanda.repinta(e.detail && e.detail.raiz));

  // La banda superior alterna entre la frase de salida y la escalera. La banda vive en un
  // grupo de secciones, así que no se puede pedir por el Section Rendering API: se
  // sincronizan solo el mensaje y la barra desde la escalera que bubu-cart.js acaba de
  // repintar en «Monta tu caja» o en la cesta. El relevo y el orden de la fila no se tocan.
  const sincronizaBanda = (lineas) => {
    const fuente = document.querySelector('[data-bubu-caja] [data-bubu-escalera], [data-bubu-cesta-cont] [data-bubu-escalera]');
    // SOLO las bandas que la página permite convertir en escalera. Liquid marca cuáles
    // con [data-bubu-tanda-esc] (home, cumples, Bubu World). En tienda, ficha y carrito
    // la banda no lleva la marca y se queda en tanda pase lo que pase: la escalera ya
    // está a la vista en esas páginas y repetirla deja dos barras de hitos en pantalla.
    document.querySelectorAll('[data-bubu-tanda][data-bubu-tanda-esc]').forEach((banda) => {
      banda.classList.toggle('is-escalera', lineas > 0);
      if (!fuente) return;
      const destino = banda.querySelector('[data-bubu-escalera]');
      if (!destino) return;
      destino.className = fuente.className.replace('bubu-esc--corta', 'bubu-esc--banda');
      ['data-uds', 'data-sig', 'data-pct'].forEach((k) => destino.setAttribute(k, fuente.getAttribute(k) || ''));
      const par = [['.bubu-esc__msg', '.bubu-esc__msg'], ['.bubu-esc__barra', '.bubu-esc__barra']];
      par.forEach(([a, b]) => {
        const o = fuente.querySelector(a);
        const d = destino.querySelector(b);
        if (o && d) d.innerHTML = o.innerHTML;
      });
    });
  };
  // Cuenta LÍNEAS DE TIENDA, no artículos: con solo un pack de cumple (o solo el
  // activador de regalo, que va a 0 €) la banda sigue siendo de tanda. Mismo criterio
  // que lineasNormales() en bubu-cart.js y que el bucle de sections/bubu-tanda.liquid.
  const lineasTienda = (c) =>
    c && c.items
      ? c.items.filter((i) => {
          const p = i.properties || {};
          return i.final_line_price !== 0 && !p._regalo && !p._pack_id;
        }).length
      : 0;
  document.addEventListener('bubu:cart', (e) => {
    const c = e.detail && e.detail.cart;
    if (c) sincronizaBanda(lineasTienda(c));
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arranca);
else arranca();

export default BubuTanda;
